import {
  ITwilioAvailableNumber,
  ITwilioMessageRow,
  ITwilioNumberPrice,
  ITwilioPhoneNumberRow,
} from '@common/interfaces/twilio'
import { combineReducers } from 'redux'
import { ITwilioMessagesParams } from '../../api/twilio'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IActionWithParams } from '../apiMiddleware'
import * as actions from './actions'

interface IListResult<T> {
  data: T[]
  total: number
}

export default combineReducers({
  phoneNumbers: createLoadingDataReducer<IListResult<ITwilioPhoneNumberRow>>([
    actions.LIST_PHONE_NUMBERS_REQUEST,
  ]),
  // Starts NOT loading, unlike the default bucket: nothing fetches this on
  // mount — it only fills when ops presses Search — and the provision modal
  // reads `isLoading` as "a search is in flight". With the default `true` the
  // modal opened saying "Searching Twilio…" and the Search button sat disabled
  // in its spinner state, so no search could ever be started.
  availableNumbers: createLoadingDataReducer<
    IListResult<ITwilioAvailableNumber> & { price: ITwilioNumberPrice | null }
  >(
    [actions.SEARCH_AVAILABLE_NUMBERS_REQUEST],
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    { isLoading: false, isSaving: false, isSaved: false, data: null },
  ),
  /** The unfiltered pool, backing the messages feed's Number dropdown. */
  numberOptions: createLoadingDataReducer<IListResult<ITwilioPhoneNumberRow>>([
    actions.LIST_NUMBER_OPTIONS_REQUEST,
  ]),
  allMessages: createLoadingDataReducer<IListResult<ITwilioMessageRow>>(
    [actions.LIST_ALL_MESSAGES_REQUEST],
    undefined,
    undefined,
    // Reset when the list unmounts, so a remount starts from page 0 rather than
    // whatever had been scrolled into view.
    [actions.CLEAR_ALL_MESSAGES],
  ),

  messages: createLoadingDataReducer<IListResult<ITwilioMessageRow>>(
    [actions.LIST_MESSAGES_REQUEST],
    undefined,
    undefined,
    // Reset on close, so the next number opened cannot inherit this feed.
    [actions.CLEAR_MESSAGES],
  ),

  /**
   * Which number the loaded `messages` actually belong to, or null when they
   * belong to nobody yet.
   *
   * These are verification codes, so rendering them under the wrong number is
   * the one thing this feed must never do — and `createLoadingDataReducer`
   * keeps `data` untouched through both REQUEST and FAILURE, so the previous
   * number's codes are still in the store while the next number is loading and
   * stay there for good if that fetch fails. Set only on SUCCESS, when the data
   * on hand demonstrably came from that number's query; the UI renders the feed
   * only while this matches the open number.
   */
  messagesFor(
    state: string | null = null,
    action: IActionWithParams<ITwilioMessagesParams, IListResult<ITwilioMessageRow>>,
  ): string | null {
    switch (action.type) {
      case actions.LIST_MESSAGES_SUCCESS:
        return action.params?.phoneNumberId || null
      case actions.LIST_MESSAGES_REQUEST:
        // A fetch for the number already on screen keeps its label: that is the
        // websocket refetch, and blanking it would flash an empty feed over
        // rows the user is reading — exactly what `skipLoader` exists to
        // prevent. A fetch for any OTHER number (or an unscoped one) clears it,
        // which is the leak this reducer is here for.
        return action.params?.phoneNumberId === state ? state : null
      // A failed fetch and a closed modal both mean what is in `data` is not
      // this number's answer.
      case actions.LIST_MESSAGES_FAILURE:
      case actions.CLEAR_MESSAGES:
        return null
      default:
        return state
    }
  },

  /**
   * Params of the all-messages list, so the websocket saga can refresh it.
   *
   * A `loadMore` fetch is deliberately not recorded: refetching one would
   * append that page a second time. So this holds the base params, and the
   * saga widens `perPage` to however many rows are loaded — a refetch without
   * `loadMore` replaces the feed, so page 0 alone would discard every page the
   * user had scrolled in.
   */
  lastAllMessagesParams(
    state: ITwilioMessagesParams | null = null,
    action: IActionWithParams<ITwilioMessagesParams & { loadMore?: boolean }>,
  ): ITwilioMessagesParams | null {
    if (action.type === actions.LIST_ALL_MESSAGES_REQUEST && !action.params?.loadMore) {
      return action.params || {}
    }
    // Cleared on unmount so the saga stops refetching a list nobody has open.
    if (action.type === actions.CLEAR_ALL_MESSAGES) {
      return null
    }
    return state
  },

  /**
   * Params of the most recent per-number message fetch, so the websocket saga
   * can refetch the same view when a code lands. Mirrors flags'
   * `lastListContext`: the TWILIO_MESSAGE_RECEIVED event carries no message body
   * (deliberately — see the constant's comment), so a refetch is the only way to
   * show the code, and it has to know which number's feed is on screen.
   */
  lastMessagesParams(
    state: ITwilioMessagesParams | null = null,
    action: IActionWithParams<ITwilioMessagesParams>,
  ): ITwilioMessagesParams | null {
    if (action.type === actions.LIST_MESSAGES_REQUEST) {
      return action.params || {}
    }
    // Cleared on close so the saga stops refetching a feed nobody has open.
    if (action.type === actions.CLEAR_MESSAGES) {
      return null
    }
    return state
  },
})
